import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { consultorService } from '@/services/consultorService'
import { oportunidadeService } from '@/services/oportunidadeService'
import { comissaoService } from '@/services/comissaoService'
import { auditoriaService } from '@/services/auditoriaService'
import {
  COR_SITUACAO_CONSULTOR,
  ConsultorDetalhe,
  IndicadoresConsultor,
  ROTULO_SITUACAO_CONSULTOR,
} from '@/types/consultor'
import { ROTULO_STATUS_CANDIDATURA } from '@/types/candidatura'
import { ETAPA_COMERCIAL_LABEL, ETAPAS_COMERCIAIS, EtapaComercial, STATUS_LABEL } from '@/types/statusReserva'
import { Oportunidade } from '@/types/oportunidade'
import { LancamentoComissao, NotaFiscalComissao, ResumoComissao, StatusComissao } from '@/types/comissao'
import { EventoAuditoria } from '@/types/auditoria'
import { PageResponse } from '@/types/comum'
import { STATUS_NOTA } from '@/pages/financeiro/notaFiscalRotulos'
import { nomeDaReserva } from '@/components/agenda/SeletorReservaReuniao'
import { formatCNPJ, formatCurrency, formatDate, formatDateTime, formatPhone } from '@/utils/formatters'
import { authStore } from '@/store/authStore'
import { rotuloCanal } from './ConsultoresListPage'

/** Valores de `?aba=`; vazio = Dados cadastrais. */
const ABAS = [
  { valor: '', rotulo: 'Dados cadastrais' },
  { valor: 'reservas', rotulo: 'Reservas' },
  { valor: 'comissoes', rotulo: 'Comissões e NFs' },
  { valor: 'onboarding', rotulo: 'Onboarding' },
  { valor: 'auditoria', rotulo: 'Auditoria' },
] as const

const STATUS_COMISSAO: Record<StatusComissao, { rotulo: string; cor: 'success' | 'info' | 'default' }> = {
  CONFIRMADA: { rotulo: 'Confirmada', cor: 'success' },
  PROJETADA: { rotulo: 'Projetada', cor: 'info' },
  CANCELADA: { rotulo: 'Cancelada', cor: 'default' },
}

const mensagemDe = (e: unknown, padrao: string): string => (e instanceof Error ? e.message : padrao)

const Campo: FC<{ rotulo: string; children: ReactNode }> = ({ rotulo, children }) => (
  <Box>
    <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
      {rotulo}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {children || '—'}
    </Typography>
  </Box>
)

const Secao: FC<{ titulo: string; children: ReactNode }> = ({ titulo, children }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        {titulo}
      </Typography>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </CardContent>
  </Card>
)

const Item: FC<{ rotulo: string; children: ReactNode; largo?: boolean }> = ({ rotulo, children, largo }) => (
  <Grid item xs={12} sm={largo ? 12 : 6}>
    <Campo rotulo={rotulo}>{children}</Campo>
  </Grid>
)

// ─── Aba 1: Dados cadastrais ────────────────────────────────────────────

const AbaDados: FC<{ c: ConsultorDetalhe }> = ({ c }) => {
  const end = c.endereco
  const banco = c.dadosBancarios
  const cand = c.candidatura
  const enderecoLinha = end
    ? [end.logradouro, end.numero, end.complemento].filter(Boolean).join(', ')
    : null
  const cidadeLinha = end ? [end.bairro, end.cidade && end.uf ? `${end.cidade}/${end.uf}` : end.cidade].filter(Boolean).join(' · ') : null
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Secao titulo="Identificação">
          <Item rotulo="Nome">{c.nome}</Item>
          <Item rotulo="CPF">{c.cpfMascarado}</Item>
          <Item rotulo="CNPJ">{c.cnpj ? formatCNPJ(c.cnpj) : null}</Item>
          <Item rotulo="Data de nascimento">{c.dataNascimento ? formatDate(c.dataNascimento) : null}</Item>
          <Item rotulo="Canal">{rotuloCanal(c.tipoCanal)}</Item>
          <Item rotulo="Empresa">{c.tenantNome}</Item>
          <Item rotulo="Conta criada em">{c.criadoEm ? formatDate(c.criadoEm) : null}</Item>
          <Item rotulo="Acesso">{c.contaHabilitada ? 'Habilitado' : 'Desabilitado'}</Item>
        </Secao>
      </Grid>
      <Grid item xs={12} md={6}>
        <Secao titulo="Contato">
          <Item rotulo="E-mail de acesso">{c.email}</Item>
          <Item rotulo="E-mail profissional">{c.contato?.emailProfissional}</Item>
          <Item rotulo="Celular">{c.contato?.celular ? formatPhone(c.contato.celular) : null}</Item>
          <Item rotulo="Telefone">{c.contato?.telefone ? formatPhone(c.contato.telefone) : null}</Item>
        </Secao>
      </Grid>
      <Grid item xs={12} md={6}>
        <Secao titulo="Endereço">
          <Item rotulo="Logradouro" largo>
            {enderecoLinha}
          </Item>
          <Item rotulo="Bairro e cidade">{cidadeLinha}</Item>
          <Item rotulo="CEP">{end?.cep}</Item>
        </Secao>
      </Grid>
      <Grid item xs={12} md={6}>
        <Secao titulo="Dados bancários">
          <Item rotulo="Banco">{banco?.banco}</Item>
          <Item rotulo="Agência">{banco?.agencia}</Item>
          <Item rotulo="Conta">{[banco?.contaMascarada, banco?.tipoConta].filter(Boolean).join(' · ')}</Item>
          <Item rotulo="Chave PIX">{banco?.chavePixMascarada}</Item>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              CPF, conta e chave PIX aparecem mascarados nesta tela.
            </Typography>
          </Grid>
        </Secao>
      </Grid>
      <Grid item xs={12}>
        {cand ? (
          <Secao titulo="Candidatura e perfil profissional">
            <Item rotulo="Status do cadastro">{ROTULO_STATUS_CANDIDATURA[cand.status] ?? cand.status}</Item>
            <Item rotulo="Origem">{cand.conviteDireto ? 'Convite direto' : 'Funil de candidatura'}</Item>
            <Item rotulo="Aceite dos termos">
              {cand.aceiteTermosEm
                ? `${formatDateTime(cand.aceiteTermosEm)}${cand.aceiteTermosVersao ? ` · versão ${cand.aceiteTermosVersao}` : ''}`
                : null}
            </Item>
            <Item rotulo="Avaliada em">{cand.avaliadoEm ? formatDateTime(cand.avaliadoEm) : null}</Item>
            <Item rotulo="Formação">{cand.formacao}</Item>
            <Item rotulo="Mercado de origem">{cand.mercadoOrigem}</Item>
            <Item rotulo="Segmentos">{cand.segmentosExperiencia}</Item>
            <Item rotulo="Região de atuação">{cand.regiaoAtuacao}</Item>
            <Item rotulo="Tipos de empresa atendidos">{cand.tiposEmpresaAtendidos}</Item>
            <Item rotulo="Produtos comercializados">{cand.produtosComercializados}</Item>
            <Item rotulo="Principal relacionamento" largo>
              {cand.principalRelacionamento}
            </Item>
            <Item rotulo="Resumo profissional" largo>
              {cand.resumoProfissional}
            </Item>
            {cand.parecer && (
              <Item rotulo="Parecer" largo>
                {cand.parecer}
              </Item>
            )}
            {!!cand.experiencias?.length && (
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                  Experiências
                </Typography>
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  {cand.experiencias.map((x, i) => (
                    <Box key={i}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {x.cargo} · {x.empresa}
                        {x.periodo ? ` (${x.periodo})` : ''}
                      </Typography>
                      {x.atividades && (
                        <Typography variant="body2" color="text.secondary">
                          {x.atividades}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Grid>
            )}
          </Secao>
        ) : (
          <Alert severity="info">
            Conta criada direto pelo administrador, sem passar pelo funil de candidatura — não há perfil profissional
            nem aceite registrado na candidatura.
          </Alert>
        )}
      </Grid>
    </Grid>
  )
}

// ─── Aba 2: Reservas ────────────────────────────────────────────────────

const AbaReservas: FC<{ consultorId: string }> = ({ consultorId }) => {
  const navigate = useNavigate()
  const [numeros, setNumeros] = useState<IndicadoresConsultor | null | undefined>(undefined)
  const [busca, setBusca] = useState('')
  const [buscaAplicada, setBuscaAplicada] = useState('')
  const [etapa, setEtapa] = useState<EtapaComercial | ''>('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [pagina, setPagina] = useState<PageResponse<Oportunidade> | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca)
      setPage(0)
    }, 400)
    return () => clearTimeout(t)
  }, [busca])

  useEffect(() => {
    consultorService
      .indicadores()
      .then((lista) => setNumeros(lista.find((i) => i.consultorId === consultorId) ?? null))
      .catch(() => setNumeros(undefined))
  }, [consultorId])

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setPagina(
        await oportunidadeService.filtrar(page, size, {
          usuarioId: consultorId,
          busca: buscaAplicada.trim(),
          etapaComercial: etapa,
        })
      )
    } catch (e) {
      setErro(mensagemDe(e, 'Falha ao carregar as reservas'))
    } finally {
      setCarregando(false)
    }
  }, [consultorId, buscaAplicada, etapa, page, size])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const linhas = pagina?.content ?? []

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Reservas" valor={numeros === undefined ? '—' : (numeros?.totalReservas ?? 0)} variante="escura" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Em andamento"
            valor={numeros === undefined ? '—' : (numeros?.emAndamento ?? 0)}
            legenda={numeros === undefined ? undefined : `${formatCurrency(numeros?.volumeEmAberto ?? 0)} em aberto`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Ganhas"
            valor={numeros === undefined ? '—' : (numeros?.ganhas ?? 0)}
            legenda={numeros === undefined ? undefined : `Conversão ${numeros?.conversaoPercentual ?? 0}%`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Pendências abertas"
            valor={numeros === undefined ? '—' : (numeros?.pendenciasAbertas ?? 0)}
            legenda={numeros === undefined ? undefined : `${numeros?.perdidas ?? 0} perdidas`}
          />
        </Grid>
      </Grid>

      {numeros !== undefined && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }} aria-label="Reservas por etapa">
          {ETAPAS_COMERCIAIS.map((e) => (
            <Chip
              key={e}
              size="small"
              variant={etapa === e ? 'filled' : 'outlined'}
              color={etapa === e ? 'primary' : 'default'}
              label={`${ETAPA_COMERCIAL_LABEL[e]}: ${numeros?.porEtapa?.[e] ?? 0}`}
              onClick={() => {
                setEtapa(etapa === e ? '' : e)
                setPage(0)
              }}
            />
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Buscar reserva"
          placeholder="Empresa, CNPJ ou RES"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          size="small"
          label="Etapa"
          value={etapa}
          onChange={(e) => {
            setEtapa(e.target.value as EtapaComercial | '')
            setPage(0)
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {ETAPAS_COMERCIAIS.map((e) => (
            <MenuItem key={e} value={e}>
              {ETAPA_COMERCIAL_LABEL[e]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {erro ? (
        <EstadoDeLista
          estado="erro"
          mensagem="Não foi possível carregar as reservas"
          detalhe={erro}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : carregando && !pagina ? (
        <EstadoDeLista estado="carregando" />
      ) : linhas.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem={buscaAplicada.trim() || etapa ? 'Nenhuma reserva com esses filtros' : 'Este consultor ainda não tem reservas'}
        />
      ) : (
        <>
          <TabelaAcao<Oportunidade>
            aria-label="Reservas do consultor"
            linhas={linhas}
            chaveDaLinha={(r) => r.id}
            onAbrir={(r) => navigate(`/oportunidades/${r.id}`)}
            colunas={[
              {
                titulo: 'Reserva',
                render: (r) => (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {nomeDaReserva(r)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.numero != null ? `RES-${String(r.numero).padStart(4, '0')} · ` : ''}
                      {r.tipoNecessidade}
                    </Typography>
                  </Box>
                ),
              },
              {
                titulo: 'Etapa',
                largura: 150,
                render: (r) => (r.etapaComercial ? ETAPA_COMERCIAL_LABEL[r.etapaComercial] : '—'),
              },
              {
                titulo: 'Status',
                largura: 200,
                render: (r) => (r.status ? (STATUS_LABEL[r.status] ?? r.status) : '—'),
              },
              {
                titulo: 'Valor estimado',
                align: 'right',
                largura: 150,
                render: (r) => formatCurrency(r.valorEstimado),
              },
              { titulo: 'Criada em', largura: 120, render: (r) => formatDate(r.createdAt) },
            ]}
          />
          <TablePagination
            component="div"
            count={pagina?.totalElements ?? 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Por página"
          />
        </>
      )}
    </Box>
  )
}

// ─── Aba 3: Comissões e NFs ─────────────────────────────────────────────

const AbaComissoes: FC<{ consultorId: string }> = ({ consultorId }) => {
  const navigate = useNavigate()
  const [resumo, setResumo] = useState<ResumoComissao | null>(null)
  const [lancamentos, setLancamentos] = useState<LancamentoComissao[] | null>(null)
  const [notas, setNotas] = useState<NotaFiscalComissao[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setErro(null)
    try {
      const [r, l, n] = await Promise.all([
        comissaoService.resumo(consultorId),
        comissaoService.extrato(undefined, consultorId),
        comissaoService.notasFiscais(consultorId),
      ])
      setResumo(r)
      setLancamentos(l)
      setNotas(n)
    } catch (e) {
      setErro(mensagemDe(e, 'Falha ao carregar comissões'))
    }
  }, [consultorId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (erro) {
    return (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar comissões e notas"
        detalhe={erro}
        acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
      />
    )
  }
  if (!resumo || !lancamentos || !notas) return <EstadoDeLista estado="carregando" />

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="Disponível para NF"
            valor={formatCurrency(resumo.disponivelParaNota)}
            legenda={`${resumo.quantidadeDisponivel} comissões confirmadas`}
            variante="escura"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            label="Projetado"
            valor={formatCurrency(resumo.projetado)}
            legenda={`${resumo.quantidadeProjetada} comissões projetadas`}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Cancelado no período" valor={formatCurrency(resumo.canceladoNoPeriodo)} />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Extrato de comissões
      </Typography>
      {lancamentos.length === 0 ? (
        <EstadoDeLista estado="vazio" mensagem="Nenhuma comissão lançada para este consultor" />
      ) : (
        <TabelaAcao<LancamentoComissao>
          aria-label="Comissões do consultor"
          linhas={lancamentos}
          chaveDaLinha={(l) => l.id}
          onAbrir={(l) => navigate(`/oportunidades/${l.oportunidadeId}`)}
          colunas={[
            {
              titulo: 'Reserva',
              render: (l) =>
                `${l.numeroReserva != null ? `RES-${String(l.numeroReserva).padStart(4, '0')} · ` : ''}${l.razaoSocial ?? '—'}`,
            },
            { titulo: 'Competência', largura: 130, render: (l) => formatDate(l.competencia) },
            {
              titulo: 'Status',
              largura: 140,
              render: (l) => (
                <Chip size="small" color={STATUS_COMISSAO[l.status]?.cor ?? 'default'} label={STATUS_COMISSAO[l.status]?.rotulo ?? l.status} />
              ),
            },
            { titulo: 'Valor', align: 'right', largura: 150, render: (l) => formatCurrency(l.valorEfetivo) },
          ]}
        />
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
        Notas fiscais
      </Typography>
      {notas.length === 0 ? (
        <EstadoDeLista estado="vazio" mensagem="Nenhuma nota fiscal enviada por este consultor" />
      ) : (
        <TabelaAcao<NotaFiscalComissao>
          aria-label="Notas fiscais do consultor"
          linhas={notas}
          chaveDaLinha={(n) => n.id}
          // A conferência da nota mora no Financeiro (aba de NFs); a ficha só lista.
          onAbrir={() => navigate('/financeiro?aba=nf')}
          colunas={[
            { titulo: 'Número', render: (n) => n.numero },
            { titulo: 'Emissão', largura: 120, render: (n) => formatDate(n.dataEmissao) },
            {
              titulo: 'Status',
              largura: 240,
              render: (n) => (
                <Chip size="small" color={STATUS_NOTA[n.status]?.cor ?? 'default'} label={STATUS_NOTA[n.status]?.rotulo ?? n.status} />
              ),
            },
            { titulo: 'Valor', align: 'right', largura: 150, render: (n) => formatCurrency(n.valor) },
          ]}
        />
      )}
    </Box>
  )
}

// ─── Aba 4: Onboarding ──────────────────────────────────────────────────

const AbaOnboarding: FC<{ c: ConsultorDetalhe }> = ({ c }) => {
  const etapas = c.onboarding ?? []
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Trilha do primeiro acesso
            </Typography>
            {etapas.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sem registro da trilha.
              </Typography>
            ) : (
              <Stack spacing={1.25} component="ol" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                {etapas.map((e) => (
                  <Stack key={e.etapa} component="li" direction="row" spacing={1.5} alignItems="center">
                    {e.concluidaEm ? (
                      <CheckCircleIcon color="success" fontSize="small" aria-label="Concluída" />
                    ) : (
                      <RadioButtonUncheckedIcon color="disabled" fontSize="small" aria-label="Pendente" />
                    )}
                    <Box>
                      <Typography variant="body2">{e.titulo}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {e.concluidaEm ? `Concluída em ${formatDateTime(e.concluidaEm)}` : 'Ainda não vista'}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Secao titulo="Candidatura">
          {c.candidatura ? (
            <>
              <Item rotulo="Status atual">{ROTULO_STATUS_CANDIDATURA[c.candidatura.status] ?? c.candidatura.status}</Item>
              <Item rotulo="Recebida em">{c.candidatura.createdAt ? formatDateTime(c.candidatura.createdAt) : null}</Item>
              <Item rotulo="Avaliada em">{c.candidatura.avaliadoEm ? formatDateTime(c.candidatura.avaliadoEm) : null}</Item>
              <Item rotulo="Termos aceitos em">
                {c.candidatura.aceiteTermosEm ? formatDateTime(c.candidatura.aceiteTermosEm) : null}
              </Item>
            </>
          ) : (
            <Item rotulo="Status" largo>
              Sem candidatura (conta criada pelo administrador)
            </Item>
          )}
        </Secao>
      </Grid>
    </Grid>
  )
}

// ─── Aba 5: Auditoria ───────────────────────────────────────────────────

const AbaAuditoria: FC<{ consultorId: string }> = ({ consultorId }) => {
  const [pagina, setPagina] = useState<PageResponse<EventoAuditoria> | null>(null)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [erro, setErro] = useState<string | null>(null)
  const [detalhe, setDetalhe] = useState<EventoAuditoria | null>(null)

  const carregar = useCallback(async () => {
    setErro(null)
    try {
      setPagina(await auditoriaService.listar({ usuarioId: consultorId, page, size }))
    } catch (e) {
      setErro(mensagemDe(e, 'Falha ao carregar a auditoria'))
    }
  }, [consultorId, page, size])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (erro) {
    return (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar a auditoria"
        detalhe={erro}
        acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
      />
    )
  }
  if (!pagina) return <EstadoDeLista estado="carregando" />
  if (pagina.content.length === 0) {
    return <EstadoDeLista estado="vazio" mensagem="Nenhuma ação deste consultor registrada" />
  }
  return (
    <>
      <TabelaAcao<EventoAuditoria>
        aria-label="Auditoria do consultor"
        linhas={pagina.content}
        chaveDaLinha={(ev) => ev.id}
        onAbrir={setDetalhe}
        colunas={[
          { titulo: 'Quando', largura: 170, render: (ev) => formatDateTime(ev.ocorridoEm) },
          { titulo: 'Ação', largura: 130, render: (ev) => ev.acao },
          {
            titulo: 'Descrição',
            render: (ev) => (
              <Box>
                <Typography variant="body2">{ev.descricao ?? ev.recurso}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {ev.modulo}
                  {ev.recursoId ? ` · ${ev.recursoId.slice(0, 8)}` : ''}
                </Typography>
              </Box>
            ),
          },
        ]}
      />
      <TablePagination
        component="div"
        count={pagina.totalElements}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={size}
        onRowsPerPageChange={(e) => {
          setSize(parseInt(e.target.value, 10))
          setPage(0)
        }}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Por página"
      />
      <Dialog open={!!detalhe} onClose={() => setDetalhe(null)} maxWidth="md" fullWidth aria-labelledby="evento-consultor-titulo">
        {detalhe && (
          <>
            <DialogTitle id="evento-consultor-titulo">
              {detalhe.acao} · {detalhe.recurso}
              <Typography variant="body2" color="text.secondary" component="div">
                {formatDateTime(detalhe.ocorridoEm)}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Item rotulo="Descrição" largo>
                  {detalhe.descricao}
                </Item>
                <Item rotulo="Módulo">{detalhe.modulo}</Item>
                <Item rotulo="Registro">{detalhe.recursoId}</Item>
                <Item rotulo="Origem (IP)">{detalhe.ip}</Item>
                <Item rotulo="Navegador">{detalhe.userAgent}</Item>
                {detalhe.detalhes && Object.keys(detalhe.detalhes).length > 0 && (
                  <Grid item xs={12}>
                    <Box component="pre" sx={{ m: 0, p: 1.5, fontSize: 12, overflow: 'auto', maxHeight: 240, bgcolor: 'action.hover', borderRadius: 1 }}>
                      {JSON.stringify(detalhe.detalhes, null, 2)}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetalhe(null)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

// ─── Página ─────────────────────────────────────────────────────────────

/**
 * Ficha do consultor (ADMIN) — `docs/PLANO_TELA_CONSULTORES.md` §3 Lote 3. Somente consulta:
 * bloquear, inativar e transferir carteira seguem nas telas que já fazem isso (atalho para
 * Usuários → Candidaturas). Cada aba busca os próprios dados só quando é aberta (`?aba=`).
 */
const ConsultorDetalhePage: FC = () => {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const ehAdmin = authStore((s) => !!s.usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN'))
  const [searchParams, setSearchParams] = useSearchParams()
  const abaParam = searchParams.get('aba') ?? ''
  const aba = ABAS.some((a) => a.valor === abaParam) ? abaParam : ''

  const [consultor, setConsultor] = useState<ConsultorDetalhe | null>(null)
  const [erro, setErro] = useState<{ mensagem: string; status?: number } | null>(null)

  const carregar = useCallback(async () => {
    setErro(null)
    setConsultor(null)
    try {
      setConsultor(await consultorService.obter(id))
    } catch (e) {
      setErro({ mensagem: mensagemDe(e, 'Falha ao carregar o consultor'), status: (e as { status?: number })?.status })
    }
  }, [id])

  useEffect(() => {
    if (ehAdmin && id) void carregar()
  }, [ehAdmin, id, carregar])

  const trocarAba = (valor: string) => {
    const proximos = new URLSearchParams(searchParams)
    if (valor) proximos.set('aba', valor)
    else proximos.delete('aba')
    setSearchParams(proximos, { replace: true })
  }

  const voltar = (
    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/consultores')}>
      Voltar
    </Button>
  )

  if (!ehAdmin) {
    return (
      <Box>
        <PageHeader title="Consultor" />
        <EstadoDeLista estado="semPermissao" detalhe="A tela de Consultores é exclusiva do administrador." />
      </Box>
    )
  }

  if (erro) {
    return (
      <Box>
        <PageHeader title="Consultor" acao={voltar} />
        {erro.status === 404 ? (
          <EstadoDeLista estado="vazio" mensagem="Consultor não encontrado" detalhe="A conta não existe ou não é de consultor." />
        ) : (
          <EstadoDeLista
            estado={erro.status === 403 ? 'semPermissao' : 'erro'}
            {...(erro.status !== 403 && {
              mensagem: 'Não foi possível carregar o consultor',
              detalhe: erro.mensagem,
              acao: { label: 'Tentar novamente', onClick: () => void carregar() },
            })}
          />
        )}
      </Box>
    )
  }

  if (!consultor) {
    return (
      <Box>
        <PageHeader title="Consultor" acao={voltar} />
        <EstadoDeLista estado="carregando" />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={consultor.nome || '(sem nome)'}
        subtitle={consultor.email ?? undefined}
        acao={
          <Stack direction="row" spacing={1}>
            {consultor.candidatura && (
              <Button variant="outlined" onClick={() => navigate('/usuarios-acessos?aba=candidaturas')}>
                Abrir candidaturas
              </Button>
            )}
            {voltar}
          </Stack>
        }
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          color={COR_SITUACAO_CONSULTOR[consultor.situacao]}
          label={ROTULO_SITUACAO_CONSULTOR[consultor.situacao] ?? consultor.situacao}
        />
        <Chip variant="outlined" label={rotuloCanal(consultor.tipoCanal)} />
        {consultor.tenantNome && <Chip variant="outlined" label={consultor.tenantNome} />}
        <Chip
          variant="outlined"
          label={
            consultor.candidatura
              ? `Cadastro: ${ROTULO_STATUS_CANDIDATURA[consultor.candidatura.status] ?? consultor.candidatura.status}`
              : 'Sem candidatura'
          }
        />
      </Stack>

      <Tabs
        value={aba}
        onChange={(_, v: string) => trocarAba(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        aria-label="Seções do consultor"
      >
        {ABAS.map((a) => (
          <Tab key={a.valor} value={a.valor} label={a.rotulo} />
        ))}
      </Tabs>

      {aba === '' && <AbaDados c={consultor} />}
      {aba === 'reservas' && <AbaReservas consultorId={consultor.id} />}
      {aba === 'comissoes' && <AbaComissoes consultorId={consultor.id} />}
      {aba === 'onboarding' && <AbaOnboarding c={consultor} />}
      {aba === 'auditoria' && <AbaAuditoria consultorId={consultor.id} />}
    </Box>
  )
}

export default ConsultorDetalhePage
